import { memo, useEffect, useState } from 'react';
import { Label } from '@/components/ui/label';
import { Input } from '@/components/ui/input';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Zap, Link2 } from 'lucide-react';
import { fetchModelRegistry, type RegistryModel } from './modelRegistryCache';



export const ScorePredictConfig = memo(({
    data,
    onChange,
    inputColumns = [],
    mode = 'score',
}: {
    data: any;
    onChange: (k: string, v: any) => void;
    inputColumns?: { name: string }[];
    mode?: 'score' | 'predict';
}) => {
    const [models, setModels] = useState<RegistryModel[]>([]);
    const [loading, setLoading] = useState(true);

    useEffect(() => {
        fetchModelRegistry().then(list => {
            setModels(list);
            setLoading(false);
        });
    }, []);

    const colNames = (Array.isArray(inputColumns) ? inputColumns : []).map(
        (c: any) => (typeof c === 'string' ? c : c?.name || c)
    ).filter(Boolean);
    const modelKey = data.modelName || data.modelId || data.globalModelName || '';
    const selectedModel = models.find((m) => m.id === modelKey || m.name === modelKey);
    const requiredFeatures = selectedModel?.feature_columns || [];
    const featureMapping = data.featureMapping || {};
    const modelTaskType = (data.taskType || data.modelTaskType || selectedModel?.model_type || 'classification').toLowerCase();

    const autoMap = () => {
        const newMap: Record<string, string> = {};
        requiredFeatures.forEach((req: string) => {
            const match = colNames.find((c: string) =>
                (c as string).toLowerCase() === req.toLowerCase() ||
                (c as string).toLowerCase().includes(req.toLowerCase())
            );
            if (match) newMap[req] = match as string;
        });
        onChange('featureMapping', newMap);
    };

    return (
        <div className="space-y-6">
            <div className="p-3 bg-indigo-50 border border-indigo-100 rounded-lg text-xs text-indigo-800">
                <h4 className="font-semibold mb-1 flex items-center gap-1">
                    <Zap className="h-3 w-3" /> {mode === 'predict' ? 'Predict' : 'Score Model'}
                </h4>
                <p>
                    {mode === 'predict'
                        ? 'Apply a trained model from the registry to your data. Select a model and map input columns to its features.'
                        : 'Uses model from upstream Train node, or select from registry. Generates predictions on your data.'}
                </p>
            </div>

            <div className="space-y-3">
                <Label className="text-xs font-semibold text-gray-700 uppercase">Model</Label>
                <Select
                    value={modelKey || '_upstream'}
                    onValueChange={(v) => {
                        const val = v === '_upstream' ? '' : v;
                        onChange('modelName', val);
                        onChange('modelId', val);
                        onChange('globalModelName', val);
                    }}
                >
                    <SelectTrigger className="w-full text-sm">
                        <SelectValue placeholder={loading ? 'Loading...' : 'Upstream or select from registry'} />
                    </SelectTrigger>
                    <SelectContent className="bg-white border border-gray-200 shadow-lg z-[100]">
                        <SelectItem value="_upstream">(Use upstream Train model)</SelectItem>
                        {models.map((m) => (
                            <SelectItem key={m.id} value={m.id}>
                                {m.name}{' '}
                                <span className="text-gray-400 text-[10px] ml-1">({m.model_type})</span>
                            </SelectItem>
                        ))}
                        {!loading && models.length === 0 && (
                            <SelectItem value="_none_registry" disabled>
                                No models in registry. Train one first.
                            </SelectItem>
                        )}
                    </SelectContent>
                </Select>
                <p className="text-[10px] text-gray-500">Leave empty to use model from connected Train node.</p>
            </div>

            <div className="space-y-3">
                <Label className="text-xs font-semibold text-gray-700 uppercase">Task Type (Optional)</Label>
                <Select
                    value={data.taskType || data.modelTaskType || modelTaskType || 'classification'}
                    onValueChange={(v) => { onChange('taskType', v); onChange('modelTaskType', v); }}
                >
                    <SelectTrigger className="w-full text-sm">
                        <SelectValue placeholder="Inferred from model or select..." />
                    </SelectTrigger>
                    <SelectContent className="bg-white border border-gray-200 shadow-lg z-[100]">
                        <SelectItem value="classification">Classification</SelectItem>
                        <SelectItem value="regression">Regression</SelectItem>
                        <SelectItem value="anomaly">Anomaly</SelectItem>
                    </SelectContent>
                </Select>
                <p className="text-[10px] text-gray-500">Set if using upstream Train model, or to override registry model type.</p>
            </div>

            {requiredFeatures.length > 0 && (
                <div className="space-y-3">
                    <div className="flex justify-between items-center">
                        <Label className="text-xs font-semibold text-gray-700 uppercase">Feature Mapping</Label>
                        <button
                            type="button"
                            onClick={autoMap}
                            className="text-[10px] text-indigo-600 hover:underline flex items-center gap-1"
                        >
                            <Link2 className="h-3 w-3" /> Auto-Map
                        </button>
                    </div>
                    <div className="border border-gray-200 rounded-lg bg-gray-50 p-2 space-y-2">
                        {requiredFeatures.map((feat: string) => (
                            <div key={feat} className="grid grid-cols-2 gap-2 items-center">
                                <span className="text-[10px] font-mono text-gray-600 truncate" title={feat}>
                                    {feat}
                                </span>
                                <Select
                                    value={featureMapping[feat] || '_unmapped'}
                                    onValueChange={(v) => {
                                        const next = { ...featureMapping };
                                        if (v === '_unmapped') delete next[feat];
                                        else next[feat] = v;
                                        onChange('featureMapping', next);
                                    }}
                                >
                                    <SelectTrigger className="h-8 text-xs">
                                        <SelectValue placeholder="Map column..." />
                                    </SelectTrigger>
                                    <SelectContent className="bg-white border border-gray-200 shadow-lg z-[100]">
                                        <SelectItem value="_unmapped">(Unmapped)</SelectItem>
                                        {colNames.map((c: string) => (
                                            <SelectItem key={c} value={c}>
                                                {c}
                                            </SelectItem>
                                        ))}
                                    </SelectContent>
                                </Select>
                            </div>
                        ))}
                    </div>
                </div>
            )}

            <div className="space-y-3">
                <Label className="text-xs font-semibold text-gray-700 uppercase">Output Column</Label>
                <Input
                    className="w-full text-sm"
                    placeholder={modelTaskType === 'regression' ? 'Score' : 'PredictedLabel'}
                    value={data.outputColumn || data.scoreColumn || (modelTaskType === 'regression' ? 'Score' : 'PredictedLabel')}
                    onChange={(e) => {
                        onChange('outputColumn', e.target.value);
                        onChange('scoreColumn', e.target.value);
                    }}
                />
                <p className="text-[10px] text-gray-500">
                    {modelTaskType === 'regression' ? 'Continuous predicted value (Score)' : modelTaskType === 'anomaly' ? '-1=anomaly, 1=normal' : 'Predicted class label'}
                </p>
            </div>

            <div className="flex items-center gap-2">
                <input
                    type="checkbox"
                    id="appendScore"
                    checked={data.appendScore !== false}
                    onChange={(e) => onChange('appendScore', e.target.checked)}
                    className="rounded border-gray-300 text-indigo-600 focus:ring-indigo-500"
                />
                <Label htmlFor="appendScore" className="text-xs font-medium">Append score to output</Label>
            </div>

            {modelTaskType === 'classification' && (
            <div className="space-y-2">
                <div className="flex items-center gap-2">
                    <input
                        type="checkbox"
                        id="probCol"
                        checked={!!data.outputProbabilityColumn}
                        onChange={(e) => onChange('outputProbabilityColumn', e.target.checked ? 'Probability' : '')}
                        className="rounded border-gray-300 text-indigo-600"
                    />
                    <Label htmlFor="probCol" className="text-xs">Add probability column (classification)</Label>
                </div>
                {data.outputProbabilityColumn && (
                    <Input
                        className="w-full text-sm"
                        placeholder="Probability"
                        value={data.outputProbabilityColumn}
                        onChange={(e) => onChange('outputProbabilityColumn', e.target.value)}
                    />
                )}
            </div>
            )}

            {modelTaskType === 'anomaly' && (
            <div className="space-y-2 p-2 bg-amber-50 border border-amber-100 rounded-lg">
                <div className="flex items-center gap-2">
                    <input
                        type="checkbox"
                        id="anomalyScoreCol"
                        checked={!!data.outputAnomalyScoreColumn}
                        onChange={(e) => onChange('outputAnomalyScoreColumn', e.target.checked ? 'AnomalyScore' : '')}
                        className="rounded border-gray-300 text-indigo-600"
                    />
                    <Label htmlFor="anomalyScoreCol" className="text-xs">Output raw anomaly score</Label>
                </div>
                {data.outputAnomalyScoreColumn && (
                    <Input
                        className="w-full text-sm"
                        placeholder="AnomalyScore"
                        value={data.outputAnomalyScoreColumn}
                        onChange={(e) => onChange('outputAnomalyScoreColumn', e.target.value)}
                    />
                )}
                <p className="text-[10px] text-gray-600">Higher score = more anomalous. Use in Evaluate for Anomaly Score Mean.</p>
            </div>
            )}

            {modelTaskType === 'regression' && (
            <p className="text-[10px] text-gray-500">Regression outputs continuous Score. Use Evaluate node for MAE, RMSE, R², MSE.</p>
            )}
        </div>
    );
});
ScorePredictConfig.displayName = 'ScorePredictConfig';
